import * as primitives from "@mirage/service-typeahead-search/service/primitives";
import { CacheKey } from "@mirage/service-typeahead-search/service/typeahead-cache";
import {
  SourceId,
  typeahead,
} from "@mirage/service-typeahead-search/service/types";
import * as wrappers from "@mirage/service-typeahead-search/service/utils/wrappers";
import * as rx from "rxjs";

import { getSourceLimit } from "@mirage/service-typeahead-search/service/utils/source-helpers";
import type { PreviousQuery } from "@mirage/shared/search/cache-result";
import * as sourceScore from "../scoring/source-score";
import { twoStageSearch } from "../search/two-stage-search";
import { getResultSection } from "../utils/section-mapper";

const FILTER_RESULT_LIMIT = 50;

export const raw: typeahead.TaggedSearch = (
  query,
  mode,
  { cache, config, grossLimit, sectionLimit },
) => {
  const sourceLimit = getSourceLimit(
    typeahead.ResultType.PreviousQuery,
    query,
    grossLimit,
    sectionLimit,
  );

  const sectionId = getResultSection(
    typeahead.ResultType.PreviousQuery,
    mode,
    query,
    config,
  );

  return rx.defer(() => {
    const allRecentQueries = cache.all(
      CacheKey.RecentQueries,
    ) as PreviousQuery[];

    if (!query.length) {
      return rx.from(
        allRecentQueries
          .map((source) => {
            const taggedResult = primitives.previousQuery(source.uuid, source, {
              sectionId,
            });
            return sourceScore.score(query, config)(taggedResult);
          })
          .sort((a, b) => b.score! - a.score!)
          .slice(0, sourceLimit),
      );
    }

    const searchableSourceCandidates = allRecentQueries.map((source) => ({
      searchableStrings: [source.query],
      originalResult: source,
    }));

    const sources = twoStageSearch(query, searchableSourceCandidates, {
      limit: FILTER_RESULT_LIMIT,
      isRecents: true,
    }).map((searchableSource) => searchableSource.originalResult);

    return rx.from(
      sources
        .map((source) => {
          const taggedResult = primitives.previousQuery(source.uuid, source, {
            sectionId,
          });
          return sourceScore.score(query, config)(taggedResult);
        })
        .sort((a, b) => b.score! - a.score!)
        .slice(0, sourceLimit),
    );
  });
};

export const search = wrappers.wrapped(SourceId.RecentQueries, raw);
